import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Info, Loader2 } from 'lucide-react'
import { usePools } from '@/hooks/use-pools'
import { useAddSystem } from '@/hooks/use-systems'
import { useIncognito } from '@/hooks/use-incognito'
import { useToast } from '@/hooks/use-toast'
import type { components } from '../../../../../types/flex-api'

type Pool = components['schemas']['Pool']
type ControlPurpose = NonNullable<NonNullable<Pool['controlConfig']>['purpose']>

const PURPOSE_LABELS: Record<ControlPurpose, string> = {
  imbalance: 'Imbalance',
  tou_stacking_poc: 'ToU Stacking',
  intraday: 'Intraday',
  intraday_cloud: 'Intraday Cloud',
}

interface AddToPoolDialogProps {
  systemID: string
  currentPoolIDs: string[]
  open: boolean
  onClose: () => void
}

export default function AddToPoolDialog({
  systemID,
  currentPoolIDs,
  open,
  onClose,
}: AddToPoolDialogProps) {
  const { data: allPools = [] } = usePools()
  const addSystem = useAddSystem()
  const incognito = useIncognito()
  const { toast } = useToast()
  const [selectedPoolID, setSelectedPoolID] = useState<string>('')
  const [activeFrom, setActiveFrom] = useState<string>('')

  // Determine which purposes the system currently participates in
  const activePurposes = new Set(
    currentPoolIDs
      .map((id) => allPools.find((p) => p.poolID === id)?.controlConfig?.purpose)
      .filter((p): p is ControlPurpose => !!p)
  )

  // Eligible pools: same purpose (or all if system has no pools yet), not already a member
  const eligiblePools = allPools.filter(
    (pool) =>
      !currentPoolIDs.includes(pool.poolID) &&
      (activePurposes.size === 0 || activePurposes.has(pool.controlConfig?.purpose as ControlPurpose))
  )

  // Group eligible pools by purpose for the select list
  const byPurpose = new Map<string, Pool[]>()
  for (const pool of eligiblePools) {
    const key = pool.controlConfig?.purpose ?? '__none__'
    if (!byPurpose.has(key)) byPurpose.set(key, [])
    byPurpose.get(key)!.push(pool)
  }

  const handleAdd = () => {
    if (!selectedPoolID) return
    const activeFromISO = activeFrom ? new Date(activeFrom).toISOString() : undefined
    addSystem.mutate(
      { poolID: selectedPoolID, systemID, activeFrom: activeFromISO },
      {
        onSuccess: () => {
          toast({ title: 'Added', description: 'System added to pool.' })
          setSelectedPoolID('')
          setActiveFrom('')
          onClose()
        },
        onError: (err) => {
          toast({ variant: 'destructive', title: 'Error', description: err.message })
        },
      }
    )
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedPoolID('')
      setActiveFrom('')
      onClose()
    }
  }

  const purposeFilterLabel =
    activePurposes.size > 0
      ? [...activePurposes].map((p) => PURPOSE_LABELS[p] ?? p).join(', ')
      : null

  const selectedPool = eligiblePools.find((p) => p.poolID === selectedPoolID)
  const needsPhaseLimitsNotice =
    selectedPool?.controlConfig?.purpose === 'imbalance' ||
    selectedPool?.controlConfig?.purpose === 'tou_stacking_poc'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add to Pool</DialogTitle>
          <DialogDescription className="font-mono text-xs">
            {incognito.uuid(systemID)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {purposeFilterLabel && (
            <p className="text-xs text-muted-foreground">
              Showing pools with purpose: <span className="font-medium text-foreground">{purposeFilterLabel}</span>
            </p>
          )}

          {eligiblePools.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No eligible pools found.
            </p>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="pool-select">Pool</Label>
              <Select value={selectedPoolID} onValueChange={setSelectedPoolID}>
                <SelectTrigger id="pool-select">
                  <SelectValue placeholder="Select a pool…" />
                </SelectTrigger>
                <SelectContent>
                  {[...byPurpose.entries()].map(([purposeKey, pools]) => (
                    <SelectGroup key={purposeKey}>
                      <SelectLabel>
                        {purposeKey === '__none__'
                          ? 'No purpose'
                          : (PURPOSE_LABELS[purposeKey as ControlPurpose] ?? purposeKey)}
                      </SelectLabel>
                      {pools.map((pool) => (
                        <SelectItem key={pool.poolID} value={pool.poolID}>
                          {incognito.poolName(pool.name)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {needsPhaseLimitsNotice && (
            <div className="flex items-start gap-2 rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                The battery system must have <strong>phase limits configured</strong> when fuse
                protection is active, or <strong>fuse protection must be disabled</strong>.
              </span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="addToPool-activeFrom">
              Active From <span className="text-muted-foreground font-normal text-xs">(Optional)</span>
            </Label>
            <input
              id="addToPool-activeFrom"
              type="datetime-local"
              value={activeFrom}
              onChange={(e) => setActiveFrom(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              When the system should start participating (your local time). Defaults to now if not set.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={addSystem.isPending}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={!selectedPoolID || addSystem.isPending}>
            {addSystem.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add to Pool
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
